import { useRef, useEffect } from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-console/box";
import Button from "@amzn/awsui-components-react/polaris/button";

import { useChatbot, Sender } from "../../context/chatbot/ChatbotContext";
import { getString } from '../../common/ui-string-labels/ui-strings-utils';
import chatbotConfig from "./chatbot-config.json";
import LoadingMessage from "./ChatbotMessages/LoadingMessage";
import BotMessage from "./ChatbotMessages/BotMessage";
import UserMessage from "./ChatbotMessages/UserMessage";
import ChatInput from "./ChatbotMessages/ChatInput";
import SupportPrompts from "./ChatbotMessages/SupportPrompts";

import './ChatbotContent.scss';

interface ChatbotContentProps {
    variant?: "widget" | "page";
}

const ChatbotContent = ({ variant = "widget" }: ChatbotContentProps) => {
    const { messages, showPrompts, isLoading, sendMessage, clearChat } = useChatbot();
    const hasConversation = messages.length > 1;
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (container) {
            const rafId = requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight;
            });
            return () => cancelAnimationFrame(rafId);
        }
    }, [messages]);

    return (
        <div className={`chatbot-layout chatbot-layout--${variant}`}>
            <div className="chatbot-messages" ref={messagesContainerRef}>
                <Box margin="xs" padding="xs">
                    <SpaceBetween size="m">
                        {messages.map((message) => {
                            if (message.loading) return <LoadingMessage key={message.id} id={message.id} />;
                            return message.sender === Sender.BOT ? (
                                <BotMessage
                                    key={message.id}
                                    message={message}
                                />
                            ) : (
                                <UserMessage key={message.id} message={message} />
                            );
                        })}
                        {showPrompts && <SupportPrompts onSend={sendMessage} />}
                        {hasConversation && !isLoading && (
                            <Box textAlign="center">
                                <Button variant="inline-link" onClick={clearChat}>
                                    {getString('chatbot.clearChat')}
                                </Button>
                            </Box>
                        )}
                    </SpaceBetween>
                </Box>
            </div>
            <div className="chatbot-footer">
                <ChatInput
                    onSend={sendMessage}
                    isLoading={isLoading}
                    maxChars={chatbotConfig.maxChars}
                />
            </div>
        </div>
    );
};

export default ChatbotContent;
